/* =============================================================
   text-marquee.js
   Place in: assets/text-marquee.js
   ============================================================= */

(function () {
  'use strict';

  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

  class TextMarquee {
    /**
     * @param {HTMLElement} el  The <section class="tm"> element
     */
    constructor(el) {
      this.el       = el;
      this.inner    = el.querySelector('.tm__inner') || el; // clipping box for overflow check
      this.track    = el.querySelector('.tm__track');
      this.listOrig = el.querySelector('.tm__list--original');

      if (!this.track || !this.listOrig) return;

      this._raf        = null;
      this._position   = 0;
      this._listWidth      = 0;   // original list width + one trailing gap (used for the loop distance)
      this._originalWidth  = 0;   // raw rendered width of the original list (no trailing gap)
      this._containerWidth = 0;   // visible width of the marquee viewport
      this._pxPerFrame = 0;
      this._running    = false;

      this._readSettings();

      /*
       * Text renders synchronously so layout is already done by window load.
       * One rAF after init ensures the browser has completed its paint so
       * scrollWidth is accurate even if fonts loaded late.
       */
      requestAnimationFrame(() => {
        this._measure();
        this._init();
      });

      /*
       * Custom fonts (e.g. 'Proxima Nova Semibold') often finish loading
       * after window.load. If the overflow check runs before the swap,
       * it measures the narrower fallback font and can wrongly conclude
       * the content fits. Re-measure once fonts are confirmed ready.
       */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this._measure();
          this._init();
        });
      }

      /* Re-measure + re-init on resize */
      this._onResize = this._debounce(() => {
        this._stopScroll();
        this._position = 0;
        this._measure();
        this._init();
      }, 250);
      window.addEventListener('resize', this._onResize);

      /* Shopify theme editor */
      this._onSectionLoadBound = this._onSectionLoad.bind(this);
      document.addEventListener('shopify:section:load', this._onSectionLoadBound);
    }

    /* ── Read data attributes ───────────────────────────────── */
    _readSettings() {
      this.autoScroll        = this.el.dataset.autoScroll === 'true';
      this.autoScrollOverflow = this.el.dataset.autoScrollOverflow === 'true';
      this.direction    = this.el.dataset.scrollDirection || 'left';
      this.speedSeconds = parseFloat(this.el.dataset.scrollSpeed) || 30;
    }

    /* ── Measure original list width + one trailing gap ─────── */
    _measure() {
      const styles    = getComputedStyle(this.el);
      const isDesktop = DESKTOP_MQ.matches;

      /*
       * Read the gap CSS variable that matches the current breakpoint.
       * These are set as inline styles on the <section> by Liquid so they
       * always reflect the live editor value.
       */
      const gapVar = isDesktop ? '--tm-gap-desktop' : '--tm-gap-mobile';
      const gap    = parseFloat(styles.getPropertyValue(gapVar)) || 0;

      /*
       * Raw rendered width of the original (single, non-cloned) list.
       * Used to check whether the content actually overflows the
       * visible container — no point animating a marquee that already
       * fits on screen.
       */
      this._originalWidth = this.listOrig.scrollWidth;

      /*
       * Visible width of the marquee's viewport. .tm has its own
       * horizontal padding, so the actual clipping box for the track
       * is .tm__inner, not .tm itself.
       */
      this._containerWidth = this.inner.clientWidth;

      /*
       * scrollWidth counts content + gaps between items but NOT a trailing
       * gap after the last item. Adding one gap makes the reset point land
       * exactly where the next clone begins — invisible, seamless seam.
       */
      this._listWidth = this._originalWidth + gap;

      if (!this._listWidth) return;

      /* px per frame to complete one full loop in speedSeconds at 60 fps */
      this._pxPerFrame = this._listWidth / (this.speedSeconds * 60);

      /* Right direction: start already offset so first visible item is correct */
      if (this.direction === 'right' && this._position === 0) {
        this._position = -this._listWidth;
      }
    }

    /* ── Start or stop ──────────────────────────────────────── */
    _init() {
      this._readSettings();

      const isDesktop = DESKTOP_MQ.matches;

      /*
       * Desktop rules (mirrors the two theme settings):
       *   - "Auto-scroll on desktop" ON        -> always scroll
       *   - that OFF, but "...when text doesn't
       *     fit" ON and content overflows       -> scroll anyway
       *   - neither applies                     -> stay static
       * Mobile keeps its previous behavior (always scrolls).
       */
      const overflows = this._originalWidth > this._containerWidth;
      let desktopShouldScroll;
      if (this.autoScroll) {
        desktopShouldScroll = true;
      } else if (this.autoScrollOverflow && overflows) {
        desktopShouldScroll = true;
      } else {
        desktopShouldScroll = false;
      }

      const shouldScroll = this._listWidth > 0 &&
        (isDesktop ? desktopShouldScroll : true);

      if (shouldScroll) {
        this._startScroll();
      } else {
        this._stopScroll();
        this.track.style.transform = '';
        this._position = 0;
      }
    }

    /* ── rAF loop ───────────────────────────────────────────── */
    _startScroll() {
      if (this._running) return;
      this._running = true;
      this._tick();
    }

    _tick() {
      if (!this._running) return;

      if (this.direction === 'right') {
        this._position += this._pxPerFrame;
        if (this._position >= 0) this._position = -this._listWidth;
      } else {
        this._position -= this._pxPerFrame;
        if (this._position <= -this._listWidth) this._position = 0;
      }

      this.track.style.transform = `translateX(${this._position}px)`;
      this._raf = requestAnimationFrame(this._tick.bind(this));
    }

    _stopScroll() {
      this._running = false;
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    /* ── Shopify editor: section reloaded ───────────────────── */
    _onSectionLoad(event) {
      if (event.target.contains(this.el)) {
        this._stopScroll();
        this._position = 0;
        this._readSettings();
        requestAnimationFrame(() => {
          this._measure();
          this._init();
        });
      }
    }

    /* ── Debounce ────────────────────────────────────────────── */
    _debounce(fn, wait) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    /* ── Destroy ────────────────────────────────────────────── */
    destroy() {
      this._stopScroll();
      this.track.style.transform = '';
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:section:load', this._onSectionLoadBound);
    }
  }

  /* ── Boot ─────────────────────────────────────────────────── */

  function initAll() {
    document.querySelectorAll('.tm').forEach((el) => {
      if (!el._textMarquee) {
        el._textMarquee = new TextMarquee(el);
      }
    });
  }

  /*
   * Run on window load so fonts are rendered and scrollWidth is accurate.
   * Guard for the case the script executes after load has already fired.
   */
  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll, { once: true });
  }

  /* Shopify editor: section added to page */
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.tm');
    if (section) {
      if (section._textMarquee) section._textMarquee.destroy();
      section._textMarquee = new TextMarquee(section);
    }
  });

  /* Shopify editor: section removed */
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.tm');
    if (section && section._textMarquee) {
      section._textMarquee.destroy();
      delete section._textMarquee;
    }
  });

})();